import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { DocumentSupportImportModel } from 'src/app/models/imports/document-support-import.model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { DocumentSupportPrintModel } from 'src/app/models/shopping/document-support-print.model';
import { DocumentSupportModel } from 'src/app/models/shopping/document-support.model';
import { environment } from 'src/environments/environment';
import { ItemFilterModel } from './../../models/filters/item-filter.model';

@Injectable({
  providedIn: 'root'
})
export class DocumentSupportService {

  private readonly _httpClient = inject(HttpClient);

  attachFilesToDocumentSupport(documentSupportId: number, files: FormData): Observable<Array<string>> {
    return this._httpClient.post<Array<string>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.AttachFilesToDocumentSupport}/${documentSupportId}`, files);
  }

  copyDocumentSupport(idDocumentSupportDestinity: number, bussinessDocumentsTypeId: number, consecutive: number): Observable<DocumentSupportModel> {
    return this._httpClient.post<DocumentSupportModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.CopyDocumentSupport}/${idDocumentSupportDestinity}/${bussinessDocumentsTypeId}/${consecutive}`, null);
  }

  createDocumentSupport(documentSupport: DocumentSupportModel): Observable<DocumentSupportModel> {
    return this._httpClient.post<DocumentSupportModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.CreateDocumentSupport}`, documentSupport);
  }

  deleteDocumentSupport(id: number): Observable<DocumentSupportModel> {
    return this._httpClient.delete<DocumentSupportModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.DeleteDocumentSupport}/${id}`);
  }

  deleteFileToDocumentSupport(documentSupportId: number, file: string): Observable<Array<string>> {
    const itemFilterResponse = new ItemFilterModel("file", null, documentSupportId, file);
    return this._httpClient.post<Array<string>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.DeleteFileToDocumentSupport}`,
      itemFilterResponse);
  }

  downloadFileToDocumentSupport(documentSupportId: number, file: string): Observable<HttpEvent<Blob>> {
    const itemFilterResponse = new ItemFilterModel("file", null, documentSupportId, file);
    return this._httpClient.request(new HttpRequest('POST', `${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.DownloadFileToDocumentSupport}`,
      itemFilterResponse, { reportProgress: true, responseType: 'blob' }));
  }

  downloadGuideImport(): Observable<HttpEvent<Blob>> {
    return this._httpClient.request(new HttpRequest('GET', `${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.DownloadGuideImport}`,
      null, { reportProgress: true, responseType: 'blob' }));
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.ExportGeneralConsultation}`, filters, {
      params, responseType: 'blob',
    });
  }

  exportImportErrors(alerts: PreInvoicingAlertModel[]): Observable<Blob> {
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.ExportImportErrors}`, alerts,
      { responseType: 'blob' }
    );
  }

  getAccountingInterfaceCode(accountingInterfaceId: number): Observable<number> {
    return this._httpClient.get<{ value: number }>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.GetAccountingInterfaceCodeAsync}/${accountingInterfaceId}`)
      .pipe(map(response => response.value)
      );
  }

  getAllBussinessDocumentsTypesForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.GetAllBussinessDocumentsTypesForSelectAsync}`,);
  }

  getDocumentSupportWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.GetAllDocumentsSupportWithPagination}`,
      filters, { params: params }
    );
  }

  getAllProvidersForSelectAsync(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.GetAllProvidersForSelectAsync}`,);
  }

  getColumnsForAttachFiles(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.GetColumnsForAttachFiles}`);
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.GetColumnsForGeneralConsultation}`);
  }

  getDocumentSupportOriginConsecutive(documentSupportId: number): Observable<number> {
    return this._httpClient.get<{ value: number }>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.GetDocumentSupportByConsecutiveAsync}/${documentSupportId}`)
      .pipe(map(response => response.value)
      );
  }

  getDocumentSupportById(id: number): Observable<DocumentSupportModel> {
    return this._httpClient.get<DocumentSupportModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.GetDocumentSupportById}/${id}`);
  }

  getDocumentSupportByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number, consecutive: number): Observable<DocumentSupportModel> {
    const params = new HttpParams().set('branchId', branchId.toString()).set('enterpriseId', enterpriseId.toString()).set('bussinessDocumentsTypeId', bussinessDocumentsTypeId.toString()).set('consecutive', consecutive.toString());
    return this._httpClient.get<DocumentSupportModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.GetDocumentSupportByKeys}`,
      { params: params }
    );
  }

  getElectronicInvoiceInterfaceCodeById(electronicInvoiceInterfaceId: number): Observable<number> {
    return this._httpClient.get<{ value: number }>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.GetElectronicInvoiceInterfaceCodeByIdAsync}/${electronicInvoiceInterfaceId}`)
      .pipe(map(response => response.value)
      );
  }

  getFilesToDocumentSupport(documentSupportId: number): Observable<any[]> {
    return this._httpClient.get<any[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.GetFilesToDocumentSupport}/${documentSupportId}`);
  }

  getFirstDocumentSupportByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number): Observable<DocumentSupportModel> {
    const params = new HttpParams().set('branchId', branchId.toString()).set('enterpriseId', enterpriseId.toString()).set('bussinessDocumentsTypeId', bussinessDocumentsTypeId.toString());
    return this._httpClient.get<DocumentSupportModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.GetFirstDocumentSupportByKeys}`, { params: params });
  }

  getLastDocumentSupportByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number): Observable<DocumentSupportModel> {
    const params = new HttpParams().set('branchId', branchId.toString()).set('enterpriseId', enterpriseId.toString()).set('bussinessDocumentsTypeId', bussinessDocumentsTypeId.toString());
    return this._httpClient.get<DocumentSupportModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.GetLastDocumentSupportByKeys}`, { params: params });
  }

  getNextDocumentSupportByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number, consecutive: number | null): Observable<DocumentSupportModel> {
    const params = new HttpParams().set('branchId', branchId.toString()).set('enterpriseId', enterpriseId.toString()).set('bussinessDocumentsTypeId', bussinessDocumentsTypeId.toString()).set('consecutive', (consecutive ?? 0).toString());
    return this._httpClient.get<DocumentSupportModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.GetNextDocumentSupportByKeys}`, { params: params });
  }

  getPreviusDocumentSupportByKeysAsync(branchId: number, enterpriseId: number, bussinessDocumentsTypeId: number, consecutive: number | null): Observable<DocumentSupportModel> {
    const params = new HttpParams().set('branchId', branchId.toString()).set('enterpriseId', enterpriseId.toString()).set('bussinessDocumentsTypeId', bussinessDocumentsTypeId.toString()).set('consecutive', (consecutive ?? 0).toString());
    return this._httpClient.get<DocumentSupportModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.GetPreviusDocumentSupportByKeys}`, { params: params });
  }

  importDocumentSupport(documentSupportsImport: Array<DocumentSupportImportModel>): Observable<LoadPreInvoicingModel> {
    return this._httpClient.post<LoadPreInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.ImportDocumentSupportsAsync}`, documentSupportsImport);
  }

  printDocumentSupport(documentSupportId: number, print: boolean): Observable<DocumentSupportPrintModel> {
    const params = new HttpParams().set('documentSupportId', documentSupportId.toString()).set('print', print);
    return this._httpClient.get<DocumentSupportPrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.PrintDocumentSupport}`,
      { params: params }
    );
  }

  setActiveDocumentSupport(documentSupportId: number): Observable<DocumentSupportModel> {
    return this._httpClient.put<DocumentSupportModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.SetActiveDocumentSupport}/${documentSupportId}`,
      null);
  }

  updateDocumentSupport(documentSupport: DocumentSupportModel): Observable<DocumentSupportModel> {
    return this._httpClient.put<DocumentSupportModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.documentSupport.UpdateDocumentSupport}`, documentSupport);
  }
}
